import React, { useEffect, useState } from 'react';
import { TextInput, TouchableOpacity, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../MyAppText';
import { translate } from '../../services/translate';
import { FieldValues, Path, useController, useFormContext } from 'react-hook-form';

function NumericRange<
    TFieldValues extends FieldValues,
    TName extends Path<TFieldValues>,
    T extends { from: number | null; to: number | null; },
>({ name }: { name: TName; }) {
    const form = useFormContext<TFieldValues>();

    const { field } = useController({
        control: form.control,
        name,
    });

    const value = field.value as T;

    const [fromValue, setFromValue] = useState<number | null>(value.from ?? null);
    const [toValue, setToValue] = useState<number | null>(value.to ?? null);

    useEffect(() => {
        field.onChange({
            from: fromValue,
            to: toValue,
        });
    }, [fromValue, toValue]);


    const { styles, theme } = useStyles(stylesheet);
    const [isInvalid, setIsInvalid] = useState(false);

    const isEmpty = fromValue === null && toValue === null;

    useEffect(() => {

        if (toValue === null || fromValue === null) {
            return setIsInvalid(false);
        }

        if (toValue < fromValue) {
            return setIsInvalid(true);
        }

        setIsInvalid(false);
    }, [fromValue, toValue]);

    return (
        <View style={styles.inputContainer}>
            <View style={styles.clearRow}>
                <TouchableOpacity onPress={() => {
                    setFromValue(null);
                    setToValue(null);
                }} disabled={isEmpty}>
                    <MyAppText style={[styles.clearSelection, isEmpty && styles.clearSelectionDisabled]}>
                        {translate('clearSelection')}
                    </MyAppText>
                </TouchableOpacity>
            </View>

            <View style={[{ flexDirection: 'row' }]}>

                <TextInput
                    keyboardType='number-pad'
                    value={fromValue !== null ? String(fromValue) : ''}
                    onChangeText={(value) => {
                        const onlyNumbers = value.replace(/[^0-9]/g, '');
                        const parsed = onlyNumbers.trim() === '' ? null : Number(onlyNumbers);
                        setFromValue(parsed);
                    }}
                    inputMode="numeric"
                    placeholder={translate('minimum')}
                    style={[styles.input, styles.inputLeft, {
                        color: fromValue === null ? theme.colors.placeholder : theme.colors.textColor
                    }, (isInvalid ? styles.invalid : null), {}]}
                />
                <MyAppText style={styles.inputAttach(isInvalid)}>{translate('to')}</MyAppText>
                <TextInput
                    keyboardType='number-pad'
                    value={toValue !== null ? String(toValue) : ''}
                    onChangeText={(value) => {
                        const onlyNumbers = value.replace(/[^0-9]/g, '');
                        const parsed = onlyNumbers.trim() === '' ? null : Number(onlyNumbers);
                        setToValue(parsed);
                    }}
                    inputMode="numeric"
                    placeholder={translate('maximumMale')}
                    style={[styles.input, styles.inputRight, {
                        color: toValue === null ? theme.colors.placeholder : theme.colors.textColor
                    }, (isInvalid ? styles.invalid : null), {}]}
                />

            </View>
            {isInvalid ? <MyAppText style={styles.error}>{translate('theValueFromLeftCantBeGreaterThanTheRightValue')}</MyAppText> : null}

        </View >
    );
}

NumericRange.getCount = (value: { from: number | null | undefined; to: number | null | undefined; }) => {
    return (value.from ?? null) !== null || (value.to ?? null) !== null ? 1 : 0;
};

export default NumericRange;

const stylesheet = createStyleSheet(theme => ({
    inputContainer: {
        minWidth: 200,
    },

    error: {
        color: theme.colors.errors['300'],
        fontSize: 10
    },

    clearRow: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
    },
    clearSelection: {
        fontSize: 14,
        fontWeight: 'bold',
        color: theme.colors.primary['500'],
    },
    clearSelectionDisabled: {
        opacity: 0.4,
    },

    inputAttach: (isInvalid: boolean) => ({
        color: isInvalid ? '#FFFFFF' : theme.colors.textColor,
        fontSize: 13,
        backgroundColor: isInvalid ? theme.colors.errors['500'] : theme.colors.container.border,
        borderTopWidth: 1,
        borderBottomWidth: 1,
        borderRightWidth: 1,
        borderColor: isInvalid ? theme.colors.errors['500'] : theme.colors.container.border,
        padding: 10,
    }),

    invalid: {
        borderColor: theme.colors.errors['500'],
    },

    input: {
        fontSize: 16,
        fontFamily: 'Open Sans',
        backgroundColor: theme.colors.field.background,
        borderColor: theme.colors.field.border,
        borderWidth: 1,
        padding: 10,
        width: 100,
    },

    inputLeft: {
        borderTopStartRadius: 4,
        borderBottomStartRadius: 4,
    },

    inputRight: {
        borderTopEndRadius: 4,
        borderBottomEndRadius: 4,
    },

    filterText: {
        color: theme.colors.labelColor,
        fontSize: 16,
        height: 25,
    },
    disabled: {
        backgroundColor: theme.colors.field.disabledBackground,
    },
}));
